import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';

import ChevronDownIcon from '@/assets/icons/chevron-down.svg';

interface IOption {
  label: string;
  value: string;
}

interface IMultiListBox {
  /** list of options */
  options: IOption[];

  /** list of selected options */
  selectedValues?: IOption[];

  /** when user select an option from dropdown, onChange callback will be invoked */
  onChange: (selected: IOption[]) => void;

  /* placeholder, only renders if default value is falsey */
  placeholder?: string;
}
export function MultiListBox({
  options,
  onChange,
  selectedValues = [],
  placeholder = 'Select options...',
}: IMultiListBox) {
  // Use value strings in state for easier comparison
  const [selectedValuesSet, setSelectedValuesSet] = useState<Set<string>>(
    new Set(selectedValues.map((item) => item.value)),
  );

  // Sync with parent component when selectedValues prop changes
  useEffect(() => {
    setSelectedValuesSet(new Set(selectedValues.map((item) => item.value)));
  }, [selectedValues]);

  // Convert selected values set to array of IOption objects
  const selectedItems = options.filter((option) => selectedValuesSet.has(option.value));

  const handleChange = (selected: IOption[]) => {
    // Create a new set of selected values
    const newSelectedValues = new Set(selected.map((item) => item.value));
    setSelectedValuesSet(newSelectedValues);

    // Call parent onChange with full option objects
    const selectedOptions = options.filter((option) =>
      newSelectedValues.has(option.value),
    );
    onChange(selectedOptions);
  };

  return (
    <Listbox value={selectedItems} onChange={handleChange} multiple>
      <div className="relative">
        <ListboxButton className="relative inline-flex justify-between w-full px-4 py-2 text-gray-700 border border-gray-100 rounded-md bg-gray focus:outline-none focus:ring-1 focus:ring-gray-500">
          <span>
            {selectedItems.length > 0
              ? selectedItems.map((item) => item.label).join(', ')
              : placeholder}
          </span>
          <img
            src={ChevronDownIcon}
            alt="chevron icon"
            className="w-5 h-5 ml-2 -mr-1"
            aria-hidden="true"
          />
        </ListboxButton>
        <ListboxOptions className="absolute z-10 w-full mt-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1 overflow-y-auto max-h-48">
            {options.map((option) => (
              <ListboxOption key={option.value} value={option} as={Fragment}>
                {({ focus, selected }) => (
                  <li
                    className={classNames(
                      'px-4 py-2 text-sm text-left cursor-pointer',
                      focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                      selected ? 'font-bold text-[#000]' : '',
                    )}
                  >
                    {option.label}
                  </li>
                )}
              </ListboxOption>
            ))}
          </div>
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
